import { formatTaskDueDate } from '../../lib/taskCadenceDates';
// src/components/clients/ClientTimeline.tsx
//
// PKT-0043a Sidebar Timeline merge v1 · Phase 1
//
// Unified chronological feed that interleaves every event source the client
// has touched. v1a renders rows in collapsed form (icon + timestamp + body).
// PKT-0043b will add expandable raw payload rows + email tracking badges +
// hover quick actions per row.
//
// Notes input folds into the top of the Timeline per Director #6 §E.4 mockup
// (matches the "📝 Add note ▼" affordance). The most recent note also
// renders as the first Timeline row (the noteToTimelineEvent path).
//
// Refs:
//   - projects/locatenowpro-client-story-redesign.md §E.4 + §E.5 (vision)
//   - live/packets/open/PKT-0043-sidebar-timeline-merge-v1.md (parent packet)

import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  TextField,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  type SelectChangeEvent,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  NoteAdd as NoteAddIcon,
  RemoveCircleOutline as SkipIcon,
} from "@mui/icons-material";
import { buildTimeline } from "../../lib/timelineEvents";
import TimelineEventRow from "./TimelineEvent";
import { useEmailTracking } from "../../hooks/useEmailTracking";
import type {
  ClientActivity,
  ClientNote,
  ClientTask,
  SavedSearch,
  SentListing,
  SentGuestCard,
} from "./types";
import type { Invoice } from "./invoiceTypes";
import { formatRelativeTime } from "./helpers";
import { taskTypeOptions, channelConfig } from "./config";
import { dueBucket } from "../../lib/taskCadenceDates";

/**
 * PKT-0065 quick-log templates · 6 options the operator picks from the
 * Type dropdown in the "Add a quick note" card. Selecting non-default
 * prefills the textarea with `prefill` and on submit writes an
 * `activities` row with `activity_type` rather than a `client_notes` row.
 *
 * All 5 non-Note types are already mapped in helpers.ts (icon + color +
 * formatActivityDescription) per PR #124 cherry-pick of the +11-type
 * extension · so the Timeline row renders correctly when the new
 * activity lands.
 */
export interface QuickLogTemplate {
  key: string;
  label: string;
  /** Empty string = Note path (client_notes write) */
  activityType: string;
  /** Empty string = no prefill (default blank textarea) */
  prefill: string;
}

export const QUICK_LOG_TEMPLATES: QuickLogTemplate[] = [
  { key: "note", label: "Note", activityType: "", prefill: "" },
  { key: "vm", label: "Left a VM", activityType: "left_voicemail", prefill: "Left a voicemail" },
  { key: "text", label: "Texted", activityType: "sent_text", prefill: "Sent text" },
  { key: "call", label: "Called", activityType: "call_logged", prefill: "Logged call" },
  { key: "email", label: "Emailed", activityType: "email_sent", prefill: "Sent email" },
  { key: "meeting", label: "Met with", activityType: "meeting_scheduled", prefill: "In-person meeting" },
];

interface ClientTimelineProps {
  taskWorkflowWritesEnabled?: boolean;
  activities: ClientActivity[];
  notes: ClientNote[];
  sentListings: SentListing[];
  savedSearches: SavedSearch[];
  sentGuestCards: SentGuestCard[];
  invoices: Invoice[];
  tasks: ClientTask[];
  isLoading?: boolean;
  loadingTasks?: boolean;
  // Inline add-note · DetailDrawer owns the supabase write + reload chain.
  // PKT-0065 v1: optional activityType param. When set, DetailDrawer writes
  // an `activities` row with that type and SKIPS the client_notes write.
  // When unset (default Note path), original client_notes + note_added
  // activity behavior preserved verbatim.
  onAddNote: (content: string, activityType?: string) => Promise<void>;
  // Client's intake wishlist (the "What does your perfect home look like?"
  // field captured at lead intake or initial client creation · displayed as a
  // pinned callout above the timeline so locators see context at a glance
  // without clicking the edit pencil). Director-direct addition 2026-05-17.
  clientWishlist?: string;
  // Per-event inline action callbacks (recovered from pre-#118 per-tab UIs)
  onOpenInvoicePdf?: (invoice: Invoice) => void;
  onResendInvoice?: (invoice: Invoice) => void;
  onResendGuestCard?: (guestCard: SentGuestCard) => void;
  onOpenListingEmail?: (listing: SentListing) => void;
  onOpenGuestCardEmail?: (guestCard: SentGuestCard) => void;
  onEditSearch?: (search: SavedSearch) => void;
  onDeleteSearch?: (searchId: string) => void;
  onAddTask: () => void;
  onCompleteTask: (taskId: string) => Promise<void> | void;
  onDeleteTask: (taskId: string) => Promise<void> | void;
  // PKT-0245 · skip a cadence step the operator deliberately passed on.
  onSkipTask?: (taskId: string) => Promise<void> | void;
}

/**
 * Strip backfill batch-tag suffixes from notes for display.
 * Format: "<human content> | historical-backfill-... (...)"
 * Returns just the human content portion.
 */
const cleanWishlist = (raw: string | undefined): string => {
  if (!raw) return "";
  // Drop everything after the first " | historical-backfill" marker
  const idx = raw.indexOf(" | historical-backfill");
  const trimmed = idx >= 0 ? raw.slice(0, idx) : raw;
  return trimmed.trim();
};

const taskTypeLabel = (type: string): string => {
  const match = taskTypeOptions?.find?.(
    (o: any) => o?.value === type || o?.id === type
  );
  if (match && (match as any).label) return (match as any).label;
  return type.replace(/_/g, " ");
};


const ClientTimeline: React.FC<ClientTimelineProps> = ({
  taskWorkflowWritesEnabled = false,
  activities,
  notes,
  sentListings,
  savedSearches,
  sentGuestCards,
  invoices,
  tasks,
  isLoading,
  loadingTasks,
  onAddNote,
  clientWishlist,
  onOpenInvoicePdf,
  onResendInvoice,
  onResendGuestCard,
  onOpenListingEmail,
  onOpenGuestCardEmail,
  onEditSearch,
  onDeleteSearch,
  onAddTask,
  onCompleteTask,
  onDeleteTask,
  onSkipTask,
}) => {
  const wishlist = cleanWishlist(clientWishlist);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  // PKT-0065: quick-log template dropdown state. Default "note" = blank
  // textarea + client_notes write (existing behavior). Non-Note options
  // prefill the textarea + route to activities table with chosen type.
  const [selectedKey, setSelectedKey] = useState<string>("note");

  const selectedTemplate =
    QUICK_LOG_TEMPLATES.find((t) => t.key === selectedKey) ??
    QUICK_LOG_TEMPLATES[0];

  const handleTemplateChange = (e: SelectChangeEvent<string>) => {
    const next = e.target.value;
    setSelectedKey(next);
    // Prefill the textarea with the new template unless the operator has
    // already typed custom content. Preserve operator edits — only overwrite
    // when the field is empty OR contains a previous template's prefill.
    const newTemplate =
      QUICK_LOG_TEMPLATES.find((t) => t.key === next) ?? QUICK_LOG_TEMPLATES[0];
    const isPriorPrefill = QUICK_LOG_TEMPLATES.some(
      (t) => t.prefill && noteDraft.trim() === t.prefill,
    );
    if (!noteDraft.trim() || isPriorPrefill) {
      setNoteDraft(newTemplate.prefill);
    }
  };

  const timeline = useMemo(
    () =>
      buildTimeline({
        activities,
        notes,
        sentListings,
        savedSearches,
        sentGuestCards,
        invoices,
      }),
    [activities, notes, sentListings, savedSearches, sentGuestCards, invoices]
  );

  // Collect all tracking IDs from email-bearing events for one batch fetch.
  // PKT-0064 v1c: also collect from activities[*].metadata.tracking_id so
  // when PKT-0054 backend wiring lands and bot/email activities start
  // carrying tracking_ids, the email_sent activity expand badge lights up
  // automatically with zero further frontend work. No-op today (no activity
  // row carries tracking_id yet) · forward-compat only.
  const trackingIds = useMemo(() => {
    const ids: string[] = [];
    for (const s of sentListings) if (s.tracking_id) ids.push(s.tracking_id);
    for (const g of sentGuestCards) if (g.tracking_id) ids.push(g.tracking_id);
    for (const a of activities) {
      const tid = (a.metadata as any)?.tracking_id;
      if (typeof tid === "string" && tid) ids.push(tid);
    }
    return ids;
  }, [sentListings, sentGuestCards, activities]);

  const { trackingMap } = useEmailTracking(trackingIds);
  const pendingTasks = useMemo(
    () =>
      tasks
        .filter((task) => task.status === "pending")
        .sort((a, b) => {
          const ad = a.due_date ? new Date(a.due_date).getTime() : Infinity;
          const bd = b.due_date ? new Date(b.due_date).getTime() : Infinity;
          if (ad !== bd) return ad - bd;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }),
    [tasks]
  );
  const nextTask = pendingTasks[0];

  const handleSubmit = async () => {
    const content = noteDraft.trim();
    if (!content) return;
    setSaving(true);
    try {
      // PKT-0065: pass activityType when a non-Note template is selected.
      // DetailDrawer routes Note → client_notes + note_added activity ·
      // non-Note → activities row with chosen type only.
      const activityType = selectedTemplate.activityType || undefined;
      await onAddNote(content, activityType);
      setNoteDraft("");
      // Reset dropdown to Note default per packet body UX spec.
      setSelectedKey("note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          mb: 1.5,
          borderRadius: 1.5,
          backgroundColor: pendingTasks.length ? "rgba(245, 158, 11, 0.06)" : "rgba(0,0,0,0.015)",
          borderColor: pendingTasks.length ? "rgba(245, 158, 11, 0.3)" : "divider",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setTasksOpen((open) => !open)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setTasksOpen((open) => !open);
              }
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.9,
            }}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                color: "text.secondary",
                transform: tasksOpen ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 120ms ease",
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 800, textTransform: "uppercase" }}
              >
                Open Tasks{pendingTasks.length ? ` (${pendingTasks.length})` : ""}
              </Typography>
              {(loadingTasks || nextTask) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {loadingTasks
                    ? "Loading..."
                    : `${nextTask?.title}${(nextTask?.due_at || nextTask?.due_date) ? ` · Due ${formatTaskDueDate((nextTask.due_at || nextTask.due_date)!)}` : ""}`}
                </Typography>
              )}
            </Box>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddTask}
            sx={{
              height: 34,
              px: 1.25,
              borderRadius: 1.25,
              textTransform: "none",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            New
          </Button>
        </Stack>
        <Collapse in={tasksOpen} timeout={160} unmountOnExit>
          <Stack spacing={1} sx={{ mt: 1.15, pt: 1.15, borderTop: "1px solid", borderColor: "divider" }}>
            {loadingTasks ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={20} />
              </Box>
            ) : pendingTasks.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No open tasks.
              </Typography>
            ) : (
              pendingTasks.map((task) => {
                const bucket = dueBucket(task.due_date);
                // A unique task FK embeds as an object; tolerate legacy array responses.
                const question = Array.isArray(task.question_payload)
                  ? task.question_payload[0]
                  : task.question_payload;
                const isCadence = task.source === "cadence";
                const isOutcome = task.source === "signal";
                const channel = task.channel
                  ? channelConfig[task.channel]
                  : undefined;
                const dueColor =
                  bucket === "overdue"
                    ? "error.main"
                    : bucket === "today"
                    ? "warning.main"
                    : "text.secondary";
                return (
                <Box
                  key={task.id}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                  }}
                >
                  {taskWorkflowWritesEnabled && <Tooltip title="Mark complete">
                    <Checkbox
                      size="small"
                      checked={false}
                      onChange={() => onCompleteTask(task.id)}
                      sx={{ p: 0.25, mt: 0.15 }}
                    />
                  </Tooltip>}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                      {channel && (
                        <Tooltip title={channel.label}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              color: "text.secondary",
                              "& svg": { fontSize: 16 },
                            }}
                          >
                            {channel.icon}
                          </Box>
                        </Tooltip>
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 650, wordBreak: "break-word" }}>
                        {task.title}
                      </Typography>
                      {isCadence ? (
                        <Chip
                          label="Cadence"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      ) : isOutcome ? (
                        <Chip label="Shortlist outcome" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                      ) : (
                        <Chip
                          label={taskTypeLabel(task.task_type)}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                    </Stack>
                    {task.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                        {task.description}
                      </Typography>
                    )}
                    {question?.question_text && !question.deleted_at && (
                      <Typography variant="body2" sx={{ mt: 0.5, p: 1, borderRadius: 1, bgcolor: "action.hover", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        “{question.question_text}”
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: dueColor, fontWeight: bucket === "overdue" || bucket === "today" ? 700 : 400 }}
                    >
                      {(task.due_at || task.due_date)
                        ? `${bucket === "overdue" ? "Overdue · " : bucket === "today" ? "Today · " : "Due "}${formatTaskDueDate((task.due_at || task.due_date)!)}`
                        : `Added ${formatRelativeTime(task.created_at)}`}
                    </Typography>
                  </Box>
                  {taskWorkflowWritesEnabled && isCadence && onSkipTask && (
                    <Tooltip title="Cancel this step with a reason">
                      <IconButton size="small" onClick={() => onSkipTask(task.id)}>
                        <SkipIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {taskWorkflowWritesEnabled && <Tooltip title="Cancel task">
                    <IconButton size="small" onClick={() => onDeleteTask(task.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>}
                </Box>
                );
              })
            )}
          </Stack>
        </Collapse>
      </Paper>

      {/* Inline add-note · folds into the top of the Timeline per §E.4 */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          mb: 1.5,
          borderRadius: 1.5,
          backgroundColor: "rgba(0,0,0,0.015)",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.15 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <NoteAddIcon sx={{ color: "text.secondary", fontSize: 18 }} />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase" }}
            >
              Quick Log
            </Typography>
          </Stack>
          {noteDraft.trim() && (
            <Button
              size="small"
              onClick={() => {
                setNoteDraft("");
                setSelectedKey("note");
              }}
              disabled={saving}
              sx={{ px: 0.5, minWidth: 0, textTransform: "none", fontSize: "0.72rem" }}
            >
              Clear
            </Button>
          )}
        </Stack>
        {/* PKT-0065: Type dropdown + textarea · stacks vertically on mobile.
            Selecting non-Note prefills the textarea with the template body
            so operators log "Left a VM" / "Sent text" / etc. in one click
            instead of typing the same phrasing 50 times a day. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.75}
          alignItems="stretch"
        >
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder={
              selectedTemplate.activityType
                ? "Add details (optional)…"
                : "What just happened with this client?"
            }
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            disabled={saving}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                minHeight: 76,
                alignItems: "flex-start",
              },
              "& .MuiInputBase-input": {
                py: 0.8,
              },
            }}
          />
          <Stack
            spacing={0.75}
            sx={{
              width: { xs: "100%", sm: 112 },
              flexShrink: 0,
            }}
          >
            <FormControl
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "background.paper",
                  height: 35,
                },
              }}
            >
              <Select
                value={selectedKey}
                onChange={handleTemplateChange}
                disabled={saving}
                aria-label="Quick-log type"
                sx={{ fontSize: "0.8rem" }}
              >
                {QUICK_LOG_TEMPLATES.map((t) => (
                  <MenuItem key={t.key} value={t.key}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit}
              disabled={!noteDraft.trim() || saving}
              sx={{
                height: 35,
                px: 1,
                borderRadius: 1.25,
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 700,
              }}
            >
              {saving
                ? "Saving…"
                : selectedTemplate.activityType
                ? `Log ${selectedTemplate.label.toLowerCase()}`
                : "Add"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Client Notes · the intake "perfect home" notes · pinned above
          timeline so locators have the brief at a glance without clicking
          the edit pencil. Director-direct 2026-05-17 per Grant feedback. */}
      {wishlist && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "rgba(124, 200, 255, 0.05)",
            borderColor: "rgba(124, 200, 255, 0.25)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontWeight: 700,
              fontSize: "0.65rem",
              display: "block",
              mb: 0.5,
            }}
          >
            Client Notes
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {wishlist}
          </Typography>
        </Paper>
      )}

      {/* Timeline feed */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : timeline.length === 0 ? (
        // Empty-state copy cherry-picked from PR #122 (WC53 PKT-0052) ·
        // locator-story framing: anticipation, not absence.
        <Box
          sx={{
            py: 4,
            px: 1,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>
            Nothing yet. The story starts with the first touch.
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mb: 0.25 }}>
            Send a text · Log a call · Add a note
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Each one shows up here in order, so the next locator (or you next
            week) can read the room at a glance.
          </Typography>
        </Box>
      ) : (
        <Stack divider={<Divider sx={{ opacity: 0.5 }} />} spacing={0.25}>
          {timeline.map((event) => (
            <TimelineEventRow
              key={event.id}
              event={event}
              trackingMap={trackingMap}
              onOpenInvoicePdf={onOpenInvoicePdf}
              onResendInvoice={onResendInvoice}
              onResendGuestCard={onResendGuestCard}
              onOpenListingEmail={onOpenListingEmail}
              onOpenGuestCardEmail={onOpenGuestCardEmail}
              onEditSearch={onEditSearch}
              onDeleteSearch={onDeleteSearch}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ClientTimeline;
